import { z } from 'zod';
import { ROLE_KEYS } from './enums.js';

export const ANNOUNCEMENT_PRIORITIES = ['normal', 'important'] as const;
/**
 * everyone = all staff + representatives; staff = employees only; representatives = all active representatives
 * (also those without a portal login — they are reached on WhatsApp/SMS only); roles / people = a selection.
 * Client accounts are never included.
 */
export const ANNOUNCEMENT_AUDIENCES = ['everyone', 'staff', 'representatives', 'roles', 'people'] as const;
export type AnnouncementAudience = (typeof ANNOUNCEMENT_AUDIENCES)[number];
/** Roles an announcement can target (staff and representatives; the client portal has no dashboard card). */
export const ANNOUNCEMENT_ROLES = ROLE_KEYS.filter((r) => r !== 'client');

/** A page in the app ("/finance/payroll") or an https link. */
const link = z
  .string()
  .trim()
  .max(500)
  .refine((v) => /^\/(?!\/)/.test(v) || /^https:\/\//i.test(v), 'Use a page in the app (starting with /) or an https:// link');

export const announcementInput = z
  .object({
    title: z.string().trim().min(3).max(140),
    message: z.string().trim().min(1).max(4000),
    link: link.nullish(),
    priority: z.enum(ANNOUNCEMENT_PRIORITIES).default('normal'),
    audience: z.enum(ANNOUNCEMENT_AUDIENCES),
    roles: z.array(z.enum(ANNOUNCEMENT_ROLES as unknown as [string, ...string[]])).max(20).default([]),
    userIds: z.array(z.string().uuid()).max(500).default([]),
    createTasks: z.boolean().default(false),
    taskDueAt: z.coerce.date().nullish(),
    expiresAt: z.coerce.date().nullish(),
    /** Also send to everyone's WhatsApp (queued; delivery report on the announcement). */
    sendWhatsApp: z.boolean().default(false),
    /** People without WhatsApp (or outside the 24h window with no template) get an SMS instead. */
    smsFallback: z.boolean().default(false),
    /** Also email everyone in the audience (Postmark; queued; delivery report on the announcement). */
    sendEmail: z.boolean().default(false),
    /** The author confirmed the recipient count in the confirmation dialog (required for WhatsApp broadcasts). */
    whatsappConfirmed: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.audience === 'roles' && !v.roles.length) ctx.addIssue({ code: 'custom', path: ['roles'], message: 'Choose at least one role' });
    if (v.audience === 'people' && !v.userIds.length) ctx.addIssue({ code: 'custom', path: ['userIds'], message: 'Choose at least one person' });
    if (v.sendWhatsApp && !v.whatsappConfirmed) ctx.addIssue({ code: 'custom', path: ['whatsappConfirmed'], message: 'Confirm the WhatsApp broadcast and its recipient count' });
    if (v.expiresAt && v.expiresAt.getTime() <= Date.now()) ctx.addIssue({ code: 'custom', path: ['expiresAt'], message: 'The expiry must be in the future' });
  });
export type AnnouncementInput = z.infer<typeof announcementInput>;

/** Inputs for the WhatsApp broadcast preview (recipient counts and message text) before posting. */
export const announcementBroadcastPreviewInput = z.object({
  title: z.string().trim().max(140).default(''),
  message: z.string().trim().max(4000).default(''),
  link: z.string().trim().max(500).nullish(),
  audience: z.enum(ANNOUNCEMENT_AUDIENCES),
  roles: z.array(z.enum(ANNOUNCEMENT_ROLES as unknown as [string, ...string[]])).max(20).default([]),
  userIds: z.array(z.string().uuid()).max(500).default([]),
  smsFallback: z.boolean().default(false),
});
export type AnnouncementBroadcastPreviewInput = z.infer<typeof announcementBroadcastPreviewInput>;

/** Per-recipient result of an announcement email copy. */
export const ANNOUNCEMENT_EMAIL_STATUSES = ['pending', 'sent', 'failed', 'no_address', 'opted_out', 'duplicate', 'skipped', 'expired'] as const;
export type AnnouncementEmailStatus = (typeof ANNOUNCEMENT_EMAIL_STATUSES)[number];
export const ANNOUNCEMENT_EMAIL_STATUS_LABELS: Record<AnnouncementEmailStatus, string> = {
  pending: 'Queued',
  sent: 'Sent',
  failed: 'Failed',
  no_address: 'No email address',
  opted_out: 'Opted out',
  duplicate: 'Same address as another recipient',
  skipped: 'Not sent',
  expired: 'Expired (not sent in time)',
};
